/**
 * Formatação pt-BR.
 *
 * O app do monorepo resolvia isto com CLDR + `@progress/kendo-react-intl`
 * carregados no boot. Aqui é `Intl` nativo — nenhuma dependência, mesmo
 * resultado, e uma regra explícita: ausência de dado NUNCA vira zero nem data
 * vazia; vira o texto "Sem dado" (Playbook § Os quatro estados, quinta regra).
 */

export const SEM_DADO = 'Sem dado';

const dataCurta = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const dataHora = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const mesExtenso = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

function parse(valor: string | null | undefined): Date | null {
  if (!valor) return null;
  // "YYYY-MM-DD" sem hora é data civil: interpretar como local, não como UTC.
  const civil = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
  if (civil) return new Date(Number(civil[1]), Number(civil[2]) - 1, Number(civil[3]));
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data;
}

export function formatarData(valor: string | null | undefined): string {
  const data = parse(valor);
  return data ? dataCurta.format(data) : SEM_DADO;
}

export function formatarDataHora(valor: string | null | undefined): string {
  const data = parse(valor);
  return data ? dataHora.format(data) : SEM_DADO;
}

export function formatarDataExtenso(valor: string | null | undefined): string {
  const data = parse(valor);
  return data ? mesExtenso.format(data) : SEM_DADO;
}

/** Texto para campo textual possivelmente ausente. Nunca devolve string vazia. */
export function texto(valor: string | null | undefined, ausente = SEM_DADO): string {
  const limpo = valor?.trim();
  return limpo ? limpo : ausente;
}

/** Data de hoje no formato aceito por `<input type="date">`. */
export function hojeISO(): string {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mes}-${dia}`;
}

/** Soma dias a uma data ISO civil, preservando o formato. */
export function somarDias(iso: string, dias: number): string {
  const data = parse(iso) ?? new Date();
  data.setDate(data.getDate() + dias);
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}

/** Soma minutos a um horário "HH:mm" (mock/estimativa local de horário real de disparo — ver `RetencaoHorario`). */
export function somarMinutos(horario: string, minutos: number): string {
  const [h, m] = horario.split(':').map(Number);
  const total = h * 60 + m + minutos;
  const hh = String(Math.floor(total / 60) % 24).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Data ISO civil (yyyy-mm-dd) para exibição/digitação pt-BR (dd/mm/aaaa). */
export function isoParaBR(iso: string | null | undefined): string {
  const data = parse(iso);
  return data ? dataCurta.format(data) : '';
}

/** dd/mm/aaaa digitado para ISO civil; `null` se não for uma data real (ex.: 31/02). */
export function brParaISO(valor: string): string | null {
  const bruto = valor.trim();
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(bruto);
  if (!match) return null;

  const dia = Number(match[1]);
  const mes = Number(match[2]);
  const ano = Number(match[3]);
  const data = new Date(ano, mes - 1, dia);
  const valida = data.getFullYear() === ano && data.getMonth() === mes - 1 && data.getDate() === dia;
  if (!valida) return null;

  return `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

/** Rótulos legíveis para a categoria técnica das novidades. */
export const CATEGORIAS: Record<string, string> = {
  fiscal: 'Fiscal',
  tesouraria_financas: 'Tesouraria e Finanças',
  departamento_pessoal: 'Departamento de Pessoal',
  recursos_humanos: 'Recursos Humanos',
  materiais: 'Materiais',
  suprimentos: 'Suprimentos',
  contabilidade: 'Contabilidade',
};

export function rotuloCategoria(valor: string): string {
  return CATEGORIAS[valor] ?? texto(valor);
}

/** O padrão de versão do PAN tem 4 grupos (ex.: `2.2601.122.0`) — o último é
 * o que mais esquecem de digitar. Só completa quando faltar EXATAMENTE esse
 * grupo final (3 grupos, todos não vazios); qualquer outro formato (2
 * grupos, texto livre, já com 4) passa intocado — isto não valida o
 * formato, só corrige o erro mais comum. */
export function sanitizarVersao(valor: string): string {
  const bruto = valor.trim();
  const grupos = bruto.split('.');
  if (grupos.length === 3 && grupos.every((grupo) => grupo !== '')) {
    return `${bruto}.0`;
  }
  return bruto;
}

/** Plural simples pt-BR: `plural(1, 'instalação', 'instalações')`. */
export function plural(quantidade: number, singular: string, plural_: string): string {
  return `${quantidade} ${quantidade === 1 ? singular : plural_}`;
}
